/**
 * app.tsx — the top-level shell: the boundary-ladder ribbon, the phase navigation,
 * the routed pipeline pages, the ⌘K command palette, the cognitive-load HUD, and
 * the streaming agent console.
 */
import { RouterView, Link, useRoute, type RouteDef } from "./lib/router.tsx";
import { useT } from "./lib/i18n.tsx";
import { CommandPalette, useCommandPalette } from "./components/command-palette.tsx";
import { BoundaryRibbon, LoadHud } from "./components/chrome.tsx";
import { Breadcrumbs, EntityPath } from "./components/breadcrumbs.tsx";
import { ScratchpadPanel } from "./genui/panel.tsx";
import { HomeRoute } from "./routes/home.tsx";
import { SessionRoute } from "./routes/session.tsx";
import { EvidenceRoute, InterpretRoute, AuthorRoute, EvaluateRoute, PackageRoute, ProjectRoute } from "./routes/work.tsx";
import { RegistryRoute } from "./routes/registry.tsx";
import { TraceRoute } from "./routes/trace.tsx";

const routes: readonly RouteDef[] = [
  { id: "home", pattern: "/", render: () => <HomeRoute /> },
  { id: "registry", pattern: "/registry", render: () => <RegistryRoute /> },
  { id: "session", pattern: "/session/:id", render: (m) => <SessionRoute sessionId={m.params["id"]!} /> },
  { id: "evidence", pattern: "/evidence", render: () => <EvidenceRoute /> },
  { id: "interpret", pattern: "/interpret", render: () => <InterpretRoute /> },
  { id: "author", pattern: "/author", render: () => <AuthorRoute /> },
  { id: "evaluate", pattern: "/evaluate", render: () => <EvaluateRoute /> },
  { id: "package", pattern: "/package", render: () => <PackageRoute /> },
  { id: "project", pattern: "/project", render: () => <ProjectRoute /> },
  { id: "trace", pattern: "/trace", render: () => <TraceRoute /> },
];

const NAV: { to: string; n: string; key: string }[] = [
  { to: "/", n: "00", key: "nav.studio" },
  { to: "/evidence", n: "01", key: "nav.evidence" },
  { to: "/interpret", n: "02", key: "nav.interpret" },
  { to: "/author", n: "04", key: "nav.author" },
  { to: "/evaluate", n: "05", key: "nav.evaluate" },
  { to: "/package", n: "06", key: "nav.package" },
  { to: "/project", n: "07", key: "nav.project" },
  { to: "/registry", n: "··", key: "nav.registry" },
  { to: "/trace", n: "··", key: "nav.trace" },
];

export function App(): JSX.Element {
  const palette = useCommandPalette();
  const path = useRoute();
  const t = useT();
  return (
    <div className="app">
      <BoundaryRibbon onPalette={() => palette.setOpen(true)} />
      <nav className="app-nav">
        <div className="app-nav-inner">
          {NAV.map((item) => {
            const active = item.to === "/" ? path === "/" : path.startsWith(item.to);
            return (
              <Link key={item.to} to={item.to} className={`nav-link ${active ? "active" : ""}`}>
                <span className="n">{item.n}</span>
                {t(item.key)}
              </Link>
            );
          })}
          <span className="nav-spacer" />
          <button className="nav-link" onClick={() => palette.setOpen(true)}>{t("nav.commands")}</button>
        </div>
      </nav>
      <Breadcrumbs />
      <main className="app-main">
        <RouterView
          routes={routes}
          notFound={(p) => (
            <div className="wrap">
              <div className="empty-note">No route for {p}. <Link to="/" className="mono" style={{ color: "var(--phosphor)" }}>Go to the studio →</Link></div>
            </div>
          )}
        />
      </main>
      <EntityPath />
      <CommandPalette open={palette.open} onClose={() => palette.setOpen(false)} />
      <LoadHud />
      <ScratchpadPanel />
    </div>
  );
}
